"use client";

import { useRef, useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetFooter,
} from "@/components/ui/sheet";
import { t } from "i18next";
import AudioRecorderUI from "../audio-recorder";
import RecorderAudioPlayer from "../audio-recorder/recorder-audio-player";
import { useToast } from "@/hooks/use-toast";
import { useAuthStore } from "@/store/auth.store";
import getDuration from "@/lib/getDuration";
import { getBlobFromUrl } from "@/lib/getBlobFromUrl";
import { useVisitStore } from "@/store/visit.store";
import ConfirmDialog from "../ConfirmDialog";
import { MicrophoneButton } from "@/components/ui/MicrophoneButton";
import { useRecordingUsageStore } from "@/store/recordingUsage.store";
import { SubscriptionOnlyButton } from "../SubscriptionOnlyButton/SubscriptionOnlyButton";
import { ValidationSuscriptionButton } from "@/common/enums/enums";
import { AlertMessage } from "../AlertMessage";
import { useAudioValidation } from "@/hooks/useAudioValidation";
import { UploadAudioButton } from "./UploadRecordButton";
import { ALLOWED_AUDIO_MIME_TYPES } from "@/common/config";
import { useRecordingSubmit } from "@/hooks/visits/useRecordingSubmit";
import { v4 as uuidv4 } from "uuid";
import { useRecordingSettingsStore } from "@/store/recordingSettings.store";
import { AdvancedSettings } from "@/components/ui/visits/AdvancedSettings";
import { Separator } from "@/components/ui/separator";
import { SwitchTranscriptionRealtime } from "../SwitchTranscriptionRealtime";

export function MultiRecordingSidebar({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}): JSX.Element {
  const { toast } = useToast();
  const [showRecorder, setShowRecorder] = useState(false);
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [audioFinished, setAudioFinished] = useState(false);
  const [recordedBlobUrl, setRecordedBlobUrl] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [transcription, setTranscription] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showCancelConfirmation, setShowCancelConfirmation] = useState(false);
  const { fetchAndSetAvailableMinutes } = useRecordingUsageStore.getState();
  const { uploadMultiRecordingFile, loadingMultiRecordingFile, getFile, file } =
    useVisitStore();
  const { userData } = useAuthStore.getState();
  const alreadyCompletedRef = useRef<boolean>(false);
  const { error, validateFile, resetError } = useAudioValidation();
  const {
    submitRecording,
    loading: submitRecordingLoading,
    error: submitRecordingError,
  } = useRecordingSubmit();
  const { inputLanguage, saveIsRealtimeEnabled } = useRecordingSettingsStore();

  const isCreatingRecording =
    submitRecordingLoading || loadingMultiRecordingFile;

  const handlePreventCloseOnInteraction = (event: Event): void => {
    if (showRecorder || isFileUpload || submitting) {
      event.preventDefault();
    }
  };

  const handleRecordingComplete = (
    blobUrl: string,
    transcription: string
  ): void => {
    if (alreadyCompletedRef.current) return;
    alreadyCompletedRef.current = true;

    setRecordedBlobUrl(blobUrl);
    setAudioFinished(true);
    setTranscription(transcription);
    if (userData) void fetchAndSetAvailableMinutes(userData.id);
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const fileUploaded = event.target.files?.[0];
    if (fileUploaded && validateFile(fileUploaded)) {
      const fileUrl = URL.createObjectURL(fileUploaded);
      setRecordedBlobUrl(fileUrl);
      setAudioFinished(true);
      setIsFileUpload(true);
      if (userData) void fetchAndSetAvailableMinutes(userData.id);
    }
  };

  useEffect(() => {
    if (submitting && !isCreatingRecording) {
      if (!submitRecordingError) {
        toast({
          description: t(
            "visit.sidebar.multiRecording.messages.uploadSuccessful.description"
          ),
        });
        onOpenChange(false);
      } else {
        toast({
          variant: "error",
          title: t("visit.sidebar.multiRecording.messages.requestFailed.title"),
          description: t(
            "visit.sidebar.multiRecording.messages.requestFailed.description"
          ),
        });
      }
      setSubmitting(false);
    }
  }, [
    submitting,
    submitRecordingError,
    toast,
    onOpenChange,
    isCreatingRecording,
  ]);

  const handleLegacySubmit = async (): Promise<void> => {
    if (!recordedBlobUrl) return;
    setSubmitting(true);
    try {
      const audioBlob = await getBlobFromUrl(recordedBlobUrl);
      const duration = await getDuration(audioBlob);
      if (userData?.id && file?.id) {
        await uploadMultiRecordingFile({
          file: audioBlob,
          fileId: file.id,
          userId: userData.id,
          duration,
        });
        toast({
          description: t(
            "visit.sidebar.multiRecording.messages.uploadSuccessful.description"
          ),
        });
        void getFile({ userId: userData.id, fileId: file.id });
        onOpenChange(false);
      } else {
        toast({
          variant: "error",
          title: t(
            "visit.sidebar.multiRecording.messages.userFileFailed.title"
          ),
          description: t(
            "visit.sidebar.multiRecording.messages.userFileFailed.description"
          ),
        });
      }
    } catch {
      toast({
        variant: "error",
        title: t("visit.sidebar.multiRecording.messages.requestFailed.title"),
        description: t(
          "visit.sidebar.multiRecording.messages.requestFailed.description"
        ),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (): Promise<void> => {
    if (!recordedBlobUrl) return;
    setSubmitting(true);
    if (transcription) {
      if (userData?.id && file?.id) {
        await submitRecording({
          fileId: file?.id,
          name: uuidv4(),
          duration: file.duration,
          language: inputLanguage,
          transcription,
          userId: userData.id,
        });
      }
    } else {
      await handleLegacySubmit();
    }
  };

  const resetStates = (): void => {
    setShowRecorder(false);
    setIsFileUpload(false);
    setAudioFinished(false);
    setRecordedBlobUrl(null);
    setSubmitting(false);
    alreadyCompletedRef.current = false;
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!open) {
      resetStates();
      resetError();
    }
  }, [open, resetError]);

  useEffect(() => {
    const previous = useRecordingSettingsStore.getState().isRealtimeEnabled;
    saveIsRealtimeEnabled(false);
    return (): void => {
      saveIsRealtimeEnabled(previous);
    };
  }, [saveIsRealtimeEnabled]);

  return (
    <Sheet
      open={open}
      onOpenChange={(isOpen) => {
        if (!loadingMultiRecordingFile) {
          onOpenChange(isOpen);
        }
      }}
    >
      <SheetContent
        className="w-full sm:max-w-md p-0 sm:rounded-l-2xl"
        onEscapeKeyDown={handlePreventCloseOnInteraction}
        onInteractOutside={handlePreventCloseOnInteraction}
      >
        <SheetHeader />
        <input
          ref={fileInputRef}
          accept={ALLOWED_AUDIO_MIME_TYPES.join(",")}
          style={{ display: "none" }}
          type="file"
          onChange={handleFileChange}
        />
        <div className="flex flex-col flex-1 h-full">
          <div className="flex-1 flex flex-col items-center justify-center gap-8 py-6 px-10">
            {isFileUpload ? (
              recordedBlobUrl && (
                <RecorderAudioPlayer mediaBlobUrl={recordedBlobUrl} />
              )
            ) : showRecorder ? (
              <AudioRecorderUI
                onFinished={setAudioFinished}
                onRecordingComplete={handleRecordingComplete}
              />
            ) : (
              <>
                <MicrophoneButton
                  onClick={() => {
                    setShowRecorder(true);
                  }}
                />
                <div className="flex flex-col w-full gap-4">
                  <AdvancedSettings
                    advancedSettingsEnabled={false}
                    mobileDevicesEnabled={false}
                  />
                  <Button
                    className="w-full"
                    size="xl"
                    onClick={() => {
                      setShowRecorder(true);
                    }}
                  >
                    {t("visits.sidebar.recordAudio")}
                  </Button>
                  <SwitchTranscriptionRealtime />
                </div>
                <Separator />
                <UploadAudioButton
                  onClick={() => fileInputRef.current?.click()}
                />
                {error && (
                  <AlertMessage
                    description={error}
                    title={t("error.formRecorder.titleMessage")}
                    variant="destructive"
                  />
                )}
              </>
            )}
          </div>
          <SheetFooter className="bg-tinted-grey-1 rounded-bl-2xl flex flex-col sm:space-x-0 sm:flex-col sm:justify-center content-center px-10 py-6 gap-2">
            {audioFinished && (
              <SubscriptionOnlyButton
                className="w-full"
                disabled={isCreatingRecording || submitting}
                size={"xl"}
                validations={[
                  ValidationSuscriptionButton.PLAN_FREE_TRIAL,
                  ValidationSuscriptionButton.RECORDING_USAGES,
                ]}
                onClick={handleSubmit}
              >
                {isCreatingRecording || submitting
                  ? `${t("visit.sidebar.multiRecording.processing")}...`
                  : t("visit.sidebar.multiRecording.submit")}
              </SubscriptionOnlyButton>
            )}
            <ConfirmDialog
              description={t("visits.sidebar.cancelConfirmation.description")}
              open={showCancelConfirmation}
              title={t("visits.sidebar.cancelConfirmation.title")}
              trigger={
                <Button
                  className="w-full"
                  disabled={isCreatingRecording || submitting}
                  size={"xl"}
                  variant={"grey"}
                  onClick={() => {
                    setShowCancelConfirmation(true);
                  }}
                >
                  {t("visits.sidebar.cancel")}
                </Button>
              }
              onOpenChange={setShowCancelConfirmation}
              onConfirm={() => {
                if (!isCreatingRecording && !submitting) {
                  resetStates();
                  onOpenChange(false);
                }
              }}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  );
}
